import React, { useEffect, useState, useContext, useRef } from "react";
import { useParams } from "react-router-dom";
import useFetch from "Hooks/useFetch";
import usePost from "Hooks/usePost";
import CourseSessions from "./Sessions/CourseSessions";
import Container from "Components/Container/Container";
import Slider from "Components/Slider";
import Spinner from "Components/RequestHandler/Spinner";
import { LoginContext } from "context/Auth/LoginContext";
import ApiVideoPlayer from "@api.video/react-player";
import { useTranslation } from "react-i18next";
import BASE_URL, { URL } from "Utilities/BASE_URL";
import { DownloadSimple } from "@phosphor-icons/react";

const CourseDetailes = () => {
  const { t } = useTranslation();
  const { slug } = useParams();
  const { data, error, fetchData, isLoading } = useFetch();
  const { postData } = usePost();
  const { isLoggedIn, token } = useContext(LoginContext);
  const [selectedSession, setSelectedSession] = useState(null);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [isVideoLoading, setIsVideoLoading] = useState(true);
  const [currentVideoId, setCurrentVideoId] = useState(null);
  const [watchedSessions, setWatchedSessions] = useState([]);
  const [courseProgress, setCourseProgress] = useState(0);
  const hasMarkedWatchedRef = useRef(false);
  const selectedSessionRef = useRef(null);

  useEffect(() => {
    if (isLoggedIn) {
      fetchData(`courses/${slug}`, token);
    } else {
      fetchData(`courses/${slug}`);
    }
  }, [slug, token]);

  // Fetch watched sessions when course data is loaded
  useEffect(() => {
    const fetchWatchedSessions = async () => {
      if (data?.data?._id && isLoggedIn && data?.data?.isPurchased) {
        try {
          const response = await fetch(
            `${BASE_URL}/en/courses/session/watched/${data.data._id}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          if (response.ok) {
            const result = await response.json();
            if (result.success) {
              setWatchedSessions(result.data.watchedSessions || []);
              setCourseProgress(result.data.progress || 0);
            }
          }
        } catch (error) {
          console.error("Error fetching watched sessions:", error);
        }
      }
    };
    fetchWatchedSessions();
  }, [data?.data?._id, isLoggedIn, token, data?.data?.isPurchased]);

  // Keep ref in sync with state and reset the hasMarkedWatched flag when selected session changes
  useEffect(() => {
    selectedSessionRef.current = selectedSession;
    hasMarkedWatchedRef.current = false;
  }, [selectedSession]);

  // Handle video end - mark session as watched
  const handleVideoEnded = async () => {
    // Use ref to get current session (avoids stale closure issue)
    const currentSession = selectedSessionRef.current;

    if (!currentSession || !data?.data?._id || !isLoggedIn || hasMarkedWatchedRef.current) {
      return;
    }

    // Check if already watched
    const isAlreadyWatched = watchedSessions.some(
      (w) => w.sessionId === currentSession._id || w.sessionId?.toString() === currentSession._id
    );
    if (isAlreadyWatched) {
      return;
    }

    hasMarkedWatchedRef.current = true;

    try {
      const result = await postData(
        "courses/session/watched",
        {
          courseId: data.data._id,
          sessionId: currentSession._id,
        },
        token
      );

      if (result?.success) {
        setWatchedSessions(result.data.watchedSessions || []);
        setCourseProgress(result.data.progress || 0);
      }
    } catch (error) {
      console.error("Error marking session as watched:", error);
      hasMarkedWatchedRef.current = false;
    }
  };

  const isPurchased = data?.data?.isPurchased || false;

  // Determine which video to show
  const shouldShowSessionVideo =
    isPurchased && selectedSession && selectedSession.videoId;
  const videoId = shouldShowSessionVideo
    ? selectedSession.videoId
    : data?.data?.videoTrailer?.videoId;

  const thumbnailUrl = shouldShowSessionVideo
    ? selectedSession.videoFullData?.assets?.thumbnail ||
      selectedSession.videoMetadata?.thumbnail
    : data?.data?.videoTrailer?.assets?.thumbnail;

  // Reset loading state when video changes
  useEffect(() => {
    if (videoId !== currentVideoId) {
      setIsVideoReady(false);
      setIsVideoLoading(true);
      setCurrentVideoId(videoId);
    }
  }, [videoId, currentVideoId]);

  if (isLoading) {
    return (
      <div className="flex justify-center mt-10 min-h-screen">
        <Spinner />
      </div>
    );
  }

  if (data) {
    return (
      <main className="py-10">
        <Container>
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 mb-16">
            {/* Video & Content Column - First on all screens but on mobile video leads, on desktop keeps whole column layout */}
            <div className="col-span-1 xl:col-span-9 order-1">
              {/* Video Player */}
              <div className="w-full">
                {!isVideoReady && !isVideoLoading && (
                  <div className="">
                    <img
                      src={thumbnailUrl}
                      alt={t("courses.videoThumbnail")}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                {videoId && (
                  <ApiVideoPlayer
                    video={{ id: videoId }}
                    hideTitle
                    responsive
                    onReady={() => setIsVideoReady(true)}
                    onEnded={handleVideoEnded}
                    autoplay={false}
                  />
                )}
              </div>

              {/* Selected session info */}
              {shouldShowSessionVideo && (
                <div className="bg-blue-50 p-3 mt-4 mb-4 rounded">
                  <h3 className="text-lg xl:text-xl font-semibold text-primary">
                    {selectedSession.title}
                  </h3>
                  <p className="text-sm xl:text-base text-gray-700">
                    {selectedSession.description}
                  </p>
                </div>
              )}

              {/* Sessions Column - Shows between video and content on small screens only */}
              <div className="block xl:hidden order-2 mt-6 mb-6">
                {/* PDF download for purchased users (mobile) */}
                {isPurchased && data?.data?.pdf && (
                  <div className="bg-white border rounded-lg p-4 shadow-sm mb-4">
                    <h3 className="text-base xl:text-lg font-semibold text-primary mb-1">
                      Course Resources
                    </h3>
                    <p className="text-sm text-gray-600 mb-3">
                      Download course materials (PDF)
                    </p>
                    <a
                      href={`${URL}/${data.data.pdf}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 w-full bg-primary text-white hover:bg-primary/90 px-4 py-2 rounded-md"
                    >
                      <DownloadSimple size={18} weight="bold" />
                      Download Course PDF
                    </a>
                  </div>
                )}
                <CourseSessions
                  data={data?.data}
                  isPurchased={isPurchased}
                  setSelectedSession={setSelectedSession}
                  selectedSession={selectedSession}
                  watchedSessions={watchedSessions}
                  courseProgress={courseProgress}
                />
              </div>

              {/* Course Content */}
              <div className="font-text order-3 xl:order-2">
                <div className="mt-4">
                  <p className="text-primary font-medium">
                    {data?.data?.subCategory?.title}
                  </p>
                  <h1 className="font-title text-2xl xl:text-3xl xl:text-4xl">
                    {data?.data?.title}
                  </h1>
                </div>

                <div className="flex flex-col gap-6 xl:gap-8 mt-6 xl:mt-10">
                  {[
                    data?.data?.courseBrief,
                    data?.data?.courseOutline,
                    data?.data?.courseInfo,
                    data?.data?.description,
                  ].map((section, index) => {
                    if (!section?.title && !section?.text) return null;

                    return (
                      <div key={index}>
                        {section.title && (
                          <h2 className="text-xl xl:text-2xl xl:text-3xl text-primary font-medium mb-2">
                            {section.title}
                          </h2>
                        )}
                        {section.text && (
                          <div
                            className="prose max-w-none text-sm xl:text-base"
                            dangerouslySetInnerHTML={{ __html: section.text }}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Sessions Column - Hidden on mobile, shows on desktop in same position as before */}
            <div className="col-span-1 xl:col-span-3 order-2 hidden xl:block">
              {/* PDF download for purchased users (desktop) */}
              {isPurchased && data?.data?.pdf && (
                <div className="bg-white border rounded-lg p-4 shadow-sm mb-4">
                  <h3 className="text-base xl:text-lg font-semibold text-primary mb-1">
                    Course Resources
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Download course materials (PDF)
                  </p>
                  <a
                    href={`${URL}/${data.data.pdf}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 w-full bg-primary text-white hover:bg-primary/90 px-4 py-2 rounded-md"
                  >
                    <DownloadSimple size={18} weight="bold" />
                    Download Course PDF
                  </a>
                </div>
              )}
              <CourseSessions
                data={data?.data}
                isPurchased={isPurchased}
                setSelectedSession={setSelectedSession}
                selectedSession={selectedSession}
                watchedSessions={watchedSessions}
                courseProgress={courseProgress}
              />
            </div>
          </div>

          {/* Newly Added Courses slider */}
          {data?.data?.newlyAddedCourses && (
            <Slider
              data={data.data.newlyAddedCourses}
              title={t("courses.newlyAddedCourses")}
              subTitle={t("courses.mightBeInterested")}
              type="course"
            />
          )}
        </Container>
      </main>
    );
  }
};

export default CourseDetailes;
